import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { collection, deleteField, doc, getDocs, query, updateDoc, where } from 'firebase/firestore';
import { useNavigate } from 'react-router-dom';
import { db } from '../../../shared/storage/firebase';
import { useAuth } from '../../auth/contexts/AuthContext';
import {
  AvailabilityDayTemplate,
  AvailabilityMaybeReason,
  AvailabilityQuarterKey,
  AvailabilityStatus,
  AvailabilityTimeBlock,
  AvailabilityWeekTemplate,
  TeacherAvailabilityQuarterTemplate,
  TeacherAvailabilityException,
} from '../../../types';
import '../styles/TeacherAvailabilityPage.css';
import FullCalendar from '@fullcalendar/react';
import dayGridPlugin from '@fullcalendar/daygrid';
import interactionPlugin from '@fullcalendar/interaction';
import { createDateInZone, DEFAULT_TZ, formatTimeInZone, getTimeZoneLabel } from '../../../shared/utils/timezone';
import ConfirmationModal from './ConfirmationModal';
import RequiredTeacherLogisticsModal from './RequiredTeacherLogisticsModal';
import { cancelSubRequestForSquadEvent, requestSubForSquadEvent, type SubRequestReason, type SubStatus } from '../services/subbing';
import { resolveEffectiveEventStaffing, type ActiveCoverage } from '../utils/effectiveEventStaffing';

const COLORBLIND_ACCESSIBLE_STORAGE_KEY = 'teacherAvailability.colorblindAccessible';

type QuarterDef = {
  key: AvailabilityQuarterKey;
  label: string;
  emoji: string;
  prevKey?: AvailabilityQuarterKey;
};

const QUARTERS: QuarterDef[] = [
  { key: 'winter2026', label: 'Winter (Jan–Mar 2026)', emoji: '❄️' },
  { key: 'spring2026', label: 'Spring (Apr–Jun 2026)', emoji: '🌷', prevKey: 'winter2026' },
  { key: 'summer2026', label: 'Summer (Jul–Aug 2026)', emoji: '☀️', prevKey: 'spring2026' },
  { key: 'fall2026', label: 'Fall (Sep–Dec 2026)', emoji: '🍂', prevKey: 'summer2026' },
  { key: 'winter2027', label: 'Winter (Jan–Mar 2027)', emoji: '❄️', prevKey: 'fall2026' },
  { key: 'spring2027', label: 'Spring (Apr–Jun 2027)', emoji: '🌷', prevKey: 'winter2027' },
];

const DAYS: Array<{ key: keyof AvailabilityWeekTemplate; label: string; windowLabel: string; start: string; end: string }> = [
  { key: 'sun', label: 'Sun', windowLabel: '5:00 AM–11:00 PM', start: '05:00', end: '23:00' },
  { key: 'mon', label: 'Mon', windowLabel: '5:00 AM–11:00 PM', start: '05:00', end: '23:00' },
  { key: 'tue', label: 'Tue', windowLabel: '5:00 AM–11:00 PM', start: '05:00', end: '23:00' },
  { key: 'wed', label: 'Wed', windowLabel: '5:00 AM–11:00 PM', start: '05:00', end: '23:00' },
  { key: 'thu', label: 'Thu', windowLabel: '5:00 AM–11:00 PM', start: '05:00', end: '23:00' },
  { key: 'fri', label: 'Fri', windowLabel: '5:00 AM–11:00 PM', start: '05:00', end: '23:00' },
  { key: 'sat', label: 'Sat', windowLabel: '5:00 AM–11:00 PM', start: '05:00', end: '23:00' },
];

const MAYBE_REASONS: Array<{ value: AvailabilityMaybeReason; label: string }> = [
  { value: 'schedule_conflict', label: 'Schedule conflict' },
  { value: 'travel', label: 'Travel' },
  { value: 'transportation', label: 'Transportation' },
  { value: 'health', label: 'Health' },
  { value: 'other', label: 'Other' },
];

function currentAvailabilityQuarterKey(d = new Date()): AvailabilityQuarterKey {
  const y = d.getFullYear();
  const m = d.getMonth() + 1;
  if (y === 2026 && m >= 4 && m <= 6) return 'spring2026';
  if (y === 2026 && m >= 7 && m <= 8) return 'summer2026';
  if (y === 2026 && m >= 9 && m <= 12) return 'fall2026';
  if (y === 2027 && m >= 1 && m <= 3) return 'winter2027';
  if (y === 2027 && m >= 4 && m <= 6) return 'spring2027';
  if (y > 2027 || (y === 2027 && m > 6)) return 'spring2027';
  return 'winter2026';
}

function nextAvailabilityQuarterKey(d = new Date()): AvailabilityQuarterKey {
  const current = currentAvailabilityQuarterKey(d);
  const currentIndex = QUARTERS.findIndex((quarter) => quarter.key === current);
  return QUARTERS[currentIndex + 1]?.key || current;
}

type TeacherScheduleEvent = {
  id: string;
  title: string;
  start: Date;
  end: Date;
  squadId: string;
  mainTeacherId?: string | null;
  supportingTeacherIds?: string[];
  subStatus?: SubStatus;
  subRequestedByTeacherId?: string | null;
  subAssignedTeacherId?: string | null;
  activeCoverage?: ActiveCoverage[];
};

type ExceptionDraft = {
  status: AvailabilityStatus;
  maybeReason: AvailabilityMaybeReason | null;
  maybeNote: string;
  start: string; // "HH:mm" (interpreted in teacherTz)
  end: string;   // "HH:mm" (interpreted in teacherTz)
};

function clone<T>(v: T): T {
  return JSON.parse(JSON.stringify(v)) as T;
}

function ymdFromUtcDate(d: Date): string {
  const y = d.getUTCFullYear();
  const m = String(d.getUTCMonth() + 1).padStart(2, '0');
  const day = String(d.getUTCDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

// FullCalendar's named-timezone mode uses UTC coercion unless an additional
// timezone plugin is installed. Passing Date instances in that mode displays
// their UTC clock values (for example, 6:30 PM ET becomes 10:30 PM). Give the
// calendar timezone-local, offset-free strings so the wall time stays correct.
function fullCalendarWallTime(d: Date, timeZone: string): string {
  return formatTimeInZone(d, timeZone, "yyyy-MM-dd'T'HH:mm:ss");
}

function minutes(t: string): number {
  const [hh, mm] = String(t || '').split(':').map(Number);
  if (!Number.isFinite(hh) || !Number.isFinite(mm)) return NaN;
  return hh * 60 + mm;
}

function isValidTime(t: string): boolean {
  const m = minutes(t);
  return Number.isFinite(m) && m >= 0 && m < 24 * 60;
}

function normalizeTimeRange(start: string, end: string): { start: string; end: string } {
  if (!isValidTime(start) || !isValidTime(end)) return { start, end };
  const startM = minutes(start);
  const endM = minutes(end);
  if (startM < endM) return { start, end };
  const [endHourRaw, endMinuteRaw] = end.split(':').map(Number);
  if (endHourRaw > 0 && endHourRaw < 12) {
    const pmEndM = endM + 12 * 60;
    if (startM < pmEndM) {
      return {
        start,
        end: `${String(endHourRaw + 12).padStart(2, '0')}:${String(endMinuteRaw || 0).padStart(2, '0')}`,
      };
    }
  }
  return { start, end };
}

function defaultDayTemplate(dayKey: keyof AvailabilityWeekTemplate): AvailabilityDayTemplate {
  return {
    // Default to NOT available until a teacher explicitly opts in.
    status: 'unavailable',
    maybeReason: null,
    maybeNote: '',
    timeDetailsEnabled: false,
    blocks: [],
  };
}

function defaultWeekTemplate(): AvailabilityWeekTemplate {
  return {
    sun: defaultDayTemplate('sun'),
    mon: defaultDayTemplate('mon'),
    tue: defaultDayTemplate('tue'),
    wed: defaultDayTemplate('wed'),
    thu: defaultDayTemplate('thu'),
    fri: defaultDayTemplate('fri'),
    sat: defaultDayTemplate('sat'),
  };
}

function ensureQuarterTemplate(q: AvailabilityQuarterKey, existing?: TeacherAvailabilityQuarterTemplate | null): TeacherAvailabilityQuarterTemplate {
  if (existing?.week) return existing;
  return {
    quarterKey: q,
    week: defaultWeekTemplate(),
  };
}

function dayHasMaybe(day: AvailabilityDayTemplate): boolean {
  if (day.status === 'maybe') return true;
  const blocks = Array.isArray(day.blocks) ? day.blocks : [];
  return blocks.some(b => b.status === 'maybe');
}

function normalizeBlocks(blocks: AvailabilityTimeBlock[]): AvailabilityTimeBlock[] {
  const cleaned = blocks
    .filter(b => b && typeof b.start === 'string' && typeof b.end === 'string' && typeof b.status === 'string')
    .map(b => ({ ...normalizeTimeRange(b.start, b.end), status: b.status as AvailabilityStatus }));
  cleaned.sort((a, b) => minutes(a.start) - minutes(b.start));
  return cleaned;
}

function validateDay(day: AvailabilityDayTemplate, dayKey: keyof AvailabilityWeekTemplate): string[] {
  const errs: string[] = [];
  const dayDef = DAYS.find(d => d.key === dayKey)!;

  if (dayHasMaybe(day) && !day.maybeReason) {
    errs.push(`${dayDef.label}: “Maybe” requires a reason.`);
  }

  // Weekly time details: single custom time range (inline in header)
  const blocks = normalizeBlocks(Array.isArray(day.blocks) ? day.blocks : []);
  const b = blocks[0] || null;
  if (b) {
    if (!isValidTime(b.start) || !isValidTime(b.end)) {
      errs.push(`${dayDef.label}: time details have an invalid time.`);
    } else {
      if (minutes(b.start) >= minutes(b.end)) errs.push(`${dayDef.label}: time details must have start before end.`);
    }
  }

  return errs;
}

const TeacherAvailabilityPage: React.FC = () => {
  const navigate = useNavigate();
  const { currentUser, userData } = useAuth();

  const userAvailabilityTemplates = (userData as any)?.availabilityTemplates;
  const userAvailabilityExceptions = (userData as any)?.availabilityExceptions;

  // Interpret availability dates/times in the teacher's configured timezone when available.
  // Fall back to platform default.
  const teacherTz = useMemo(() => {
    const tz = String((userData as any)?.timeZone || (userData as any)?.timezone || DEFAULT_TZ).trim();
    return tz || DEFAULT_TZ;
  }, [userData]);

  const teacherTzLabel = useMemo(() => getTimeZoneLabel(teacherTz), [teacherTz]);

  const teacherDateKey = useCallback((d: Date): string => {
    return formatTimeInZone(d, teacherTz, 'yyyy-MM-dd');
  }, [teacherTz]);

  const [colorblindAccessible, setColorblindAccessible] = useState<boolean>(() => {
    try {
      return window.localStorage.getItem(COLORBLIND_ACCESSIBLE_STORAGE_KEY) === '1';
    } catch {
      return false;
    }
  });

  const [quarterKey, setQuarterKey] = useState<AvailabilityQuarterKey>(() => nextAvailabilityQuarterKey());
  const [templates, setTemplates] = useState<Record<AvailabilityQuarterKey, TeacherAvailabilityQuarterTemplate>>(() => ({
    winter2026: ensureQuarterTemplate('winter2026', null),
    spring2026: ensureQuarterTemplate('spring2026', null),
    summer2026: ensureQuarterTemplate('summer2026', null),
    fall2026: ensureQuarterTemplate('fall2026', null),
    winter2027: ensureQuarterTemplate('winter2027', null),
    spring2027: ensureQuarterTemplate('spring2027', null),
  }));

  const [exceptions, setExceptions] = useState<Record<AvailabilityQuarterKey, Record<string, TeacherAvailabilityException>>>(() => ({
    winter2026: {},
    spring2026: {},
    summer2026: {},
    fall2026: {},
    winter2027: {},
    spring2027: {},
  }));

  const [teacherEvents, setTeacherEvents] = useState<TeacherScheduleEvent[]>([]);
  const [loadingEvents, setLoadingEvents] = useState(false);
  const [selectedCalendarEvent, setSelectedCalendarEvent] = useState<TeacherScheduleEvent | null>(null);

  const finalClassDayKey = useMemo(() => {
    let maxKey = '';
    for (const ev of teacherEvents) {
      if (!ev?.start || Number.isNaN(ev.start.getTime?.())) continue;
      const k = teacherDateKey(ev.start);
      if (k && k > maxKey) maxKey = k;
    }
    return maxKey || null;
  }, [teacherEvents, teacherDateKey]);

  const [dirtyByQuarter, setDirtyByQuarter] = useState<Record<AvailabilityQuarterKey, boolean>>({
    winter2026: false,
    spring2026: false,
    summer2026: false,
    fall2026: false,
    winter2027: false,
    spring2027: false,
  });
  const [saving, setSaving] = useState(false);
  const [saveMsg, setSaveMsg] = useState<string | null>(null);
  const [errors, setErrors] = useState<string[]>([]);

  const [confirmState, setConfirmState] = useState<{
    open: boolean;
    title: string;
    message: string;
    onConfirm: () => void;
    confirmLabel?: string;
    onSecondary?: () => void;
    secondaryLabel?: string;
  }>({
    open: false,
    title: '',
    message: '',
    onConfirm: () => {},
    confirmLabel: undefined,
    onSecondary: undefined,
    secondaryLabel: undefined,
  });
  useEffect(() => {
    try {
      window.localStorage.setItem(COLORBLIND_ACCESSIBLE_STORAGE_KEY, colorblindAccessible ? '1' : '0');
    } catch {
      // ignore
    }
  }, [colorblindAccessible]);

  // Hydrate from Firestore userData when it arrives/changes.
  useEffect(() => {
    const incoming = (userAvailabilityTemplates || {}) as any;
    setTemplates(prev => {
      const next: Record<AvailabilityQuarterKey, TeacherAvailabilityQuarterTemplate> = { ...prev };
      for (const q of QUARTERS) {
        const fromDb = incoming?.[q.key] as TeacherAvailabilityQuarterTemplate | undefined;
        next[q.key] = ensureQuarterTemplate(q.key, fromDb || prev[q.key]);
      }
      return next;
    });
  }, [userData?.id, userAvailabilityTemplates]);

  useEffect(() => {
    const incoming = userAvailabilityExceptions || {};
    setExceptions(prev => {
      const next: Record<AvailabilityQuarterKey, Record<string, TeacherAvailabilityException>> = { ...prev };
      for (const q of QUARTERS) {
        const m = incoming?.[q.key];
        next[q.key] = (m && typeof m === 'object') ? m : (prev[q.key] || {});
      }
      return next;
    });
  }, [userData?.id, userAvailabilityExceptions]);

  // Load scheduled classes for this teacher (read-only indicators on the exceptions calendar).
  useEffect(() => {
    const load = async () => {
      if (!currentUser?.uid) {
        setTeacherEvents([]);
        return;
      }
      try {
        setLoadingEvents(true);
        const [byMain, bySupport, bySubstitute, byParticipant] = await Promise.all([
          getDocs(query(collection(db, 'squadEvents'), where('mainTeacherId', '==', currentUser.uid))),
          getDocs(query(collection(db, 'squadEvents'), where('supportingTeacherIds', 'array-contains', currentUser.uid))),
          getDocs(query(collection(db, 'squadEvents'), where('subAssignedTeacherId', '==', currentUser.uid))),
          getDocs(query(collection(db, 'squadEvents'), where('eventParticipantIds', 'array-contains', currentUser.uid))),
        ]);

        const map = new Map<string, TeacherScheduleEvent>();
        const push = (snap: any) => {
          snap.docs.forEach((docSnap: any) => {
            const data = docSnap.data();
            const start = data.start?.toDate?.() || new Date(0);
            const end = data.end?.toDate?.() || new Date(0);
            map.set(docSnap.id, {
              id: docSnap.id,
              title: String(data.title || 'Class'),
              start,
              end,
              squadId: String(data.squadId || ''),
              mainTeacherId: typeof data.mainTeacherId === 'string' ? data.mainTeacherId : null,
              supportingTeacherIds: Array.isArray(data.supportingTeacherIds) ? data.supportingTeacherIds.map(String) : [],
              subStatus: (data.subStatus || 'none') as SubStatus,
              subRequestedByTeacherId: (typeof data.subRequestedByTeacherId === 'string' ? data.subRequestedByTeacherId : (data.subRequestedByTeacherId ?? null)),
              subAssignedTeacherId: typeof data.subAssignedTeacherId === 'string' ? data.subAssignedTeacherId : null,
              activeCoverage: Array.isArray(data.activeCoverage) ? data.activeCoverage : undefined,
            });
          });
        };
        push(byMain);
        push(bySupport);
        push(bySubstitute);
        push(byParticipant);
        setTeacherEvents(Array.from(map.values()));
      } catch (e) {
        console.error('Failed to load teacher schedule events for availability exceptions', e);
        setTeacherEvents([]);
      } finally {
        setLoadingEvents(false);
      }
    };
    load();
  }, [currentUser?.uid]);

  const active = templates[quarterKey];
  const activeWeek = active.week;
  const activeQuarter = QUARTERS.find((quarter) => quarter.key === quarterKey) || QUARTERS[0];
  const defaultQuarterKey = nextAvailabilityQuarterKey();
  const selectableQuarters = useMemo(() => {
    const currentKey = currentAvailabilityQuarterKey();
    const currentIndex = QUARTERS.findIndex((quarter) => quarter.key === currentKey);
    return QUARTERS.slice(Math.max(0, currentIndex));
  }, []);

  const isDirty = dirtyByQuarter[quarterKey] === true;

  const quarterForDate = useCallback((d: Date): AvailabilityQuarterKey | null => {
    const y = Number(formatTimeInZone(d, teacherTz, 'yyyy'));
    const m = Number(formatTimeInZone(d, teacherTz, 'M')); // 1-12
    if (y === 2026 && m >= 1 && m <= 3) return 'winter2026';
    if (y === 2026 && m >= 4 && m <= 6) return 'spring2026';
    if (y === 2026 && m >= 7 && m <= 8) return 'summer2026';
    if (y === 2026 && m >= 9 && m <= 12) return 'fall2026';
    if (y === 2027 && m >= 1 && m <= 3) return 'winter2027';
    if (y === 2027 && m >= 4 && m <= 6) return 'spring2027';
    return null;
  }, [teacherTz]);

  const quarterForDateKey = useCallback((dateKey: string): AvailabilityQuarterKey | null => {
    const [yRaw, mRaw] = String(dateKey || '').split('-');
    const y = Number(yRaw);
    const m = Number(mRaw);
    if (!Number.isFinite(y) || !Number.isFinite(m)) return null;
    if (y === 2026 && m >= 1 && m <= 3) return 'winter2026';
    if (y === 2026 && m >= 4 && m <= 6) return 'spring2026';
    if (y === 2026 && m >= 7 && m <= 8) return 'summer2026';
    if (y === 2026 && m >= 9 && m <= 12) return 'fall2026';
    if (y === 2027 && m >= 1 && m <= 3) return 'winter2027';
    if (y === 2027 && m >= 4 && m <= 6) return 'spring2027';
    return null;
  }, []);

  const hasWeeklyTemplateForQuarter = useCallback((q: AvailabilityQuarterKey): boolean => {
    // Important: `templates` is always "filled" with defaults, so use `userData` to detect if a teacher
    // actually saved a weekly template for the quarter.
    const incoming = (userAvailabilityTemplates || {}) as any;
    return !!incoming?.[q]?.week;
  }, [userAvailabilityTemplates]);

  // Exceptions calendar derives its quarter from the month being viewed (not user-selected).
  const [exceptionsViewAnchorDate, setExceptionsViewAnchorDate] = useState<Date>(() => new Date());
  const exceptionsQuarterKey = useMemo(() => quarterForDate(exceptionsViewAnchorDate), [exceptionsViewAnchorDate, quarterForDate]);
  const exceptionsWeekForColoring: AvailabilityWeekTemplate | null = useMemo(() => {
    if (!exceptionsQuarterKey) return null;
    if (!hasWeeklyTemplateForQuarter(exceptionsQuarterKey)) return null;
    return templates[exceptionsQuarterKey]?.week || null;
  }, [exceptionsQuarterKey, hasWeeklyTemplateForQuarter, templates]);

  const markDirty = (q: AvailabilityQuarterKey) => {
    setDirtyByQuarter(prev => ({ ...prev, [q]: true }));
  };

  const updateDay = (dayKey: keyof AvailabilityWeekTemplate, patch: Partial<AvailabilityDayTemplate>) => {
    setTemplates(prev => {
      const next = clone(prev);
      const current = next[quarterKey].week[dayKey];
      const merged: AvailabilityDayTemplate = { ...current, ...patch };
      // Keep time details in sync with the day-level status (single range)
      if (patch.status && merged.timeDetailsEnabled) {
        const blocks = normalizeBlocks(Array.isArray(merged.blocks) ? merged.blocks : []);
        const first = blocks[0];
        if (first) {
          merged.blocks = [{ ...first, status: patch.status }];
        }
      }
      next[quarterKey].week[dayKey] = merged;
      return next;
    });
    markDirty(quarterKey);
    setSaveMsg(null);
  };

  const updateTimeRange = (dayKey: keyof AvailabilityWeekTemplate, patch: Partial<AvailabilityTimeBlock>) => {
    setTemplates(prev => {
      const next = clone(prev);
      const day = next[quarterKey].week[dayKey];
      const dayDef = DAYS.find(d => d.key === dayKey)!;
      const blocks = normalizeBlocks(Array.isArray(day.blocks) ? day.blocks : []);
      const first = blocks[0] || { start: dayDef.start, end: dayDef.end, status: day.status };
      const patched = { ...first, ...patch, status: day.status };
      day.blocks = [{ ...patched, ...normalizeTimeRange(patched.start, patched.end) }];
      day.timeDetailsEnabled = true; // keep back-compat field truthy
      return next;
    });
    markDirty(quarterKey);
    setSaveMsg(null);
  };

  const normalizeWeekForInlineTime = (week: AvailabilityWeekTemplate): AvailabilityWeekTemplate => {
    const next = clone(week);
    for (const d of DAYS) {
      const day = next[d.key];
      const blocks = normalizeBlocks(Array.isArray(day.blocks) ? day.blocks : []);
      const first = blocks[0] || { start: d.start, end: d.end, status: day.status };
      const normalized = normalizeTimeRange(first.start, first.end);
      next[d.key] = {
        ...day,
        timeDetailsEnabled: true,
        blocks: [{ ...first, ...normalized, status: day.status }],
      };
    }
    return next;
  };

  // Ensure each day always has one editable time range (for inline editing in the header)
  useEffect(() => {
    setTemplates(prev => {
      const next = clone(prev);
      next[quarterKey].week = normalizeWeekForInlineTime(next[quarterKey].week);
      return next;
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [quarterKey]);

  const onSwitchQuarter = (nextKey: AvailabilityQuarterKey) => {
    if (nextKey === quarterKey) return;
    if (dirtyByQuarter[quarterKey]) {
      setConfirmState({
        open: true,
        title: 'Unsaved changes',
        message: 'You have unsaved changes for this season. Switch seasons anyway?',
        confirmLabel: 'Switch',
        onConfirm: () => {
          setConfirmState(s => ({ ...s, open: false }));
          setQuarterKey(nextKey);
          setErrors([]);
          setSaveMsg(null);
        },
      });
      return;
    }
    setQuarterKey(nextKey);
    setErrors([]);
    setSaveMsg(null);
  };

  const onCopyFromPrevious = () => {
    const def = QUARTERS.find(q => q.key === quarterKey);
    const prevKey = def?.prevKey;
    if (!prevKey) return;
    const prevT = templates[prevKey];
    if (!prevT?.week) return;
    setTemplates(prev => {
      const next = clone(prev);
      next[quarterKey].week = clone(prevT.week);
      return next;
    });
    markDirty(quarterKey);
    setSaveMsg(null);
  };

  const validateQuarter = (): string[] => {
    const errs: string[] = [];
    for (const d of DAYS) {
      errs.push(...validateDay(activeWeek[d.key], d.key));
    }
    return errs;
  };

  const onCancelWeeklyChanges = () => {
    // Revert only the current season back to last-saved (from userData) or defaults.
    const incoming = (userData?.availabilityTemplates || {}) as any;
    const fromDb = incoming?.[quarterKey] as TeacherAvailabilityQuarterTemplate | undefined;
    const base = ensureQuarterTemplate(quarterKey, fromDb || null);

    // Ensure inline editable range exists for each day
    const normalizedWeek = (() => {
      const next = clone(base.week);
      for (const d of DAYS) {
        const day = next[d.key];
        const blocks = normalizeBlocks(Array.isArray(day.blocks) ? day.blocks : []);
        const first = blocks[0] || { start: d.start, end: d.end, status: day.status };
        next[d.key] = { ...day, timeDetailsEnabled: true, blocks: [{ ...first, status: day.status }] };
      }
      return next;
    })();

    setTemplates(prev => ({ ...prev, [quarterKey]: { ...base, week: normalizedWeek } }));
    setDirtyByQuarter(prev => ({ ...prev, [quarterKey]: false }));
    setErrors([]);
    setSaveMsg(null);
  };

  const onSave = async () => {
    if (!currentUser?.uid) return;
    const errs = validateQuarter();
    setErrors(errs);
    if (errs.length > 0) return;

    try {
      setSaving(true);
      setSaveMsg(null);
      const payload: TeacherAvailabilityQuarterTemplate = {
        quarterKey,
        week: clone(activeWeek),
        updatedAt: new Date().toISOString(),
      };

      await updateDoc(doc(db, 'users', currentUser.uid), {
        [`availabilityTemplates.${quarterKey}`]: payload,
      } as any);

      setDirtyByQuarter(prev => ({ ...prev, [quarterKey]: false }));
      setSaveMsg('Saved!');
    } catch (e) {
      console.error('Failed to save availability template', e);
      setErrors(['Failed to save. Please try again.']);
    } finally {
      setSaving(false);
    }
  };

  const onBack = () => {
    if (dirtyByQuarter[quarterKey]) {
      setConfirmState({
        open: true,
        title: 'Unsaved changes',
        message: 'You have unsaved changes for this season. Leave without saving?',
        confirmLabel: 'Leave',
        onConfirm: () => {
          setConfirmState(s => ({ ...s, open: false }));
          navigate('/app/squads');
        },
      });
      return;
    }
    navigate('/app/squads');
  };

  // ---- Specific Exceptions UI + persistence ----
  const [isExceptionModalOpen, setIsExceptionModalOpen] = useState(false);
  const [isConflictModalOpen, setIsConflictModalOpen] = useState(false);
  const [isSubModalOpen, setIsSubModalOpen] = useState(false);
  const [logisticsModalOpen, setLogisticsModalOpen] = useState(false);
  const [selectedDateKey, setSelectedDateKey] = useState<string | null>(null);
  const [selectedExceptionQuarterKey, setSelectedExceptionQuarterKey] = useState<AvailabilityQuarterKey | null>(null);
  const [selectedDayLabel, setSelectedDayLabel] = useState<string>('');
  const [exceptionDraft, setExceptionDraft] = useState<ExceptionDraft>({
    status: 'unavailable',
    maybeReason: null,
    maybeNote: '',
    start: '05:00',
    end: '23:00',
  });
  const [conflictEvent, setConflictEvent] = useState<TeacherScheduleEvent | null>(null);
  const [subReason, setSubReason] = useState<SubRequestReason>('schedule_conflict');
  const [subNote, setSubNote] = useState<string>('');
  const [subRequestedRole, setSubRequestedRole] = useState<'lead' | 'co_teacher'>('lead');
  const [subNoteError, setSubNoteError] = useState<string | null>(null);
  const [submittingSub, setSubmittingSub] = useState(false);

  const dayKeyForDateKey = (dateKey: string): keyof AvailabilityWeekTemplate => {
    // dateKey is in YYYY-MM-DD; create a Date in teacher TZ noon to avoid DST edge.
    const d = createDateInZone(dateKey, '12:00', teacherTz);
    const dow = formatTimeInZone(d, teacherTz, 'EEE').toLowerCase(); // sun/mon/...
    const map: Record<string, keyof AvailabilityWeekTemplate> = {
      sun: 'sun', mon: 'mon', tue: 'tue', wed: 'wed', thu: 'thu', fri: 'fri', sat: 'sat',
    };
    return map[dow] || 'mon';
  };

  const dayDefForDateKey = (dateKey: string) => {
    const dk = dayKeyForDateKey(dateKey);
    return DAYS.find(d => d.key === dk)!;
  };

  const weekTemplateForDateKey = (dateKey: string, week: AvailabilityWeekTemplate | null): AvailabilityDayTemplate | null => {
    if (!week) return null;
    const dk = dayKeyForDateKey(dateKey);
    return week[dk] || null;
  };

  const exceptionStatusForDateKey = (dateKey: string, q: AvailabilityQuarterKey | null): AvailabilityStatus | null => {
    if (!q) return null;
    const raw = exceptions[q]?.[dateKey] as any;
    if (!raw) return null;
    const s = raw?.status as AvailabilityStatus | undefined;
    if (s === 'available' || s === 'maybe' || s === 'unavailable') return s;
    if (raw?.type === 'unavailable') return 'unavailable'; // legacy
    return null;
  };

  const effectiveStatusForDateKey = (dateKey: string, q: AvailabilityQuarterKey | null): AvailabilityStatus | null => {
    const exSt = exceptionStatusForDateKey(dateKey, q);
    if (exSt) return exSt;
    const wk = weekTemplateForDateKey(dateKey, exceptionsWeekForColoring);
    return wk?.status ?? null;
  };

  const normalizeExceptionDraft = (dateKey: string, q: AvailabilityQuarterKey, raw: TeacherAvailabilityException | null): ExceptionDraft => {
    const dayDef = dayDefForDateKey(dateKey);
    const wk = weekTemplateForDateKey(dateKey, hasWeeklyTemplateForQuarter(q) ? templates[q].week : null);
    const wkBlocks = normalizeBlocks(Array.isArray(wk?.blocks) ? wk!.blocks : []);
    const wkFirst = wkBlocks[0] || { start: dayDef.start, end: dayDef.end, status: wk?.status || 'unavailable' };
    const base: ExceptionDraft = {
      status: (wk?.status || 'unavailable') as AvailabilityStatus,
      maybeReason: (wk?.maybeReason || null) as AvailabilityMaybeReason | null,
      maybeNote: String(wk?.maybeNote || ''),
      start: wkFirst.start,
      end: wkFirst.end,
    };

    if (!raw) return base;
    const ex: any = raw;
    const status: AvailabilityStatus =
      ex.status === 'available' || ex.status === 'maybe' || ex.status === 'unavailable'
        ? ex.status
        : ex.type === 'unavailable'
          ? 'unavailable'
          : base.status;
    return {
      status,
      maybeReason: (ex.maybeReason ?? base.maybeReason ?? null) as AvailabilityMaybeReason | null,
      maybeNote: String(ex.maybeNote ?? base.maybeNote ?? ''),
      start: String(ex.start || base.start || dayDef.start),
      end: String(ex.end || base.end || dayDef.end),
    };
  };

  const getExceptionRangeUtc = (dateKey: string, d: ExceptionDraft): { start: Date; end: Date; label: string } => {
    const s = d.start;
    const e = d.end;
    return { start: createDateInZone(dateKey, s, teacherTz), end: createDateInZone(dateKey, e, teacherTz), label: `${s}–${e}` };
  };

  const findConflict = (dateKey: string, d: ExceptionDraft): TeacherScheduleEvent | null => {
    const range = getExceptionRangeUtc(dateKey, d);
    const sameDayEvents = teacherEvents.filter(ev => teacherDateKey(ev.start) === dateKey);
    for (const ev of sameDayEvents) {
      const evStart = ev.start.getTime();
      const evEnd = ev.end.getTime();
      const rStart = range.start.getTime();
      const rEnd = range.end.getTime();

      const overlaps = evStart < rEnd && rStart < evEnd;
      const fullyCoveredByRange = rStart <= evStart && evEnd <= rEnd;

      // Interpretation:
      // - unavailable: the range is an unavailability window → conflict only if overlap
      // - available: the range is an availability window → conflict if class is NOT fully covered
      // - maybe: the range is a "maybe available" window → always prompt if there's a class that day
      if (d.status === 'unavailable') {
        if (overlaps) return ev;
      } else if (d.status === 'available') {
        if (!fullyCoveredByRange) return ev;
      } else {
        // maybe
        return ev;
      }
    }
    return null;
  };

  const openExceptionEditorForDateKey = (dateKey: string) => {
    const q = quarterForDateKey(dateKey);
    if (!q) {
      setErrors(['No season is configured for this month yet.']);
      return;
    }
    const existing = exceptions[q]?.[dateKey] || null;
    const labelDate = createDateInZone(dateKey, '12:00', teacherTz);
    const label = formatTimeInZone(labelDate, teacherTz, 'EEE, MMM d, yyyy');
    setSelectedDateKey(dateKey);
    setSelectedExceptionQuarterKey(q);
    setSelectedDayLabel(label);
    setExceptionDraft(normalizeExceptionDraft(dateKey, q, existing));
    setErrors([]);
    setConflictEvent(null);
    setIsExceptionModalOpen(true);
  };

  const closeExceptionEditor = () => {
    setIsExceptionModalOpen(false);
    setSelectedDateKey(null);
    setSelectedExceptionQuarterKey(null);
    setConflictEvent(null);
  };

  const openSubRequestsForDateKey = (dateKey: string): TeacherScheduleEvent[] => {
    if (!currentUser?.uid) return [];
    return teacherEvents.filter(ev =>
      teacherDateKey(ev.start) === dateKey
      && ev.subStatus === 'requested'
      && ev.subRequestedByTeacherId === currentUser.uid
    );
  };

  const performSaveException = async () => {
    if (!currentUser?.uid || !selectedDateKey || !selectedExceptionQuarterKey) return;
    const payload: TeacherAvailabilityException = {
      ...(clone({
        status: exceptionDraft.status,
        maybeReason: exceptionDraft.status === 'maybe' ? exceptionDraft.maybeReason : null,
        maybeNote: exceptionDraft.status === 'maybe' ? exceptionDraft.maybeNote : '',
        start: exceptionDraft.start,
        end: exceptionDraft.end,
      }) as any),
      updatedAt: new Date().toISOString(),
    } as any;

    await updateDoc(doc(db, 'users', currentUser.uid), {
      [`availabilityExceptions.${selectedExceptionQuarterKey}.${selectedDateKey}`]: payload,
    } as any);

    setExceptions(prev => ({
      ...prev,
      [selectedExceptionQuarterKey]: {
        ...(prev[selectedExceptionQuarterKey] || {}),
        [selectedDateKey]: payload as any,
      },
    }));

    setSaveMsg('Saved!');
    closeExceptionEditor();
  };

  const performDeleteException = async () => {
    if (!currentUser?.uid || !selectedDateKey || !selectedExceptionQuarterKey) return;
    await updateDoc(doc(db, 'users', currentUser.uid), {
      [`availabilityExceptions.${selectedExceptionQuarterKey}.${selectedDateKey}`]: deleteField(),
    } as any);
    setExceptions(prev => {
      const next = clone(prev);
      if (next[selectedExceptionQuarterKey]) {
        delete (next[selectedExceptionQuarterKey] as any)[selectedDateKey];
      }
      return next;
    });
    setSaveMsg('Deleted.');
    closeExceptionEditor();
  };

  const saveException = async () => {
    if (!currentUser?.uid || !selectedDateKey || !selectedExceptionQuarterKey) return;

    // Basic validation
    if (!isValidTime(exceptionDraft.start) || !isValidTime(exceptionDraft.end)) {
      setErrors(['Time range has an invalid time.']);
      return;
    }
    if (minutes(exceptionDraft.start) >= minutes(exceptionDraft.end)) {
      setErrors(['Time range must have start before end.']);
      return;
    }
    if (exceptionDraft.status === 'maybe' && !exceptionDraft.maybeReason) {
      setErrors(['“Maybe” requires a reason.']);
      return;
    }

    const conflict = findConflict(selectedDateKey, exceptionDraft);
    if (conflict) {
      setConflictEvent(conflict);
      setSubRequestedRole(resolveEffectiveEventStaffing(conflict).effectiveLeadTeacherId === currentUser?.uid ? 'lead' : 'co_teacher');
      setIsConflictModalOpen(true);
      return;
    }

    // If teacher is saving an "available" exception on a day where they previously requested a sub,
    // ask whether to cancel that sub request, but proceed to save either way.
    if (exceptionDraft.status === 'available') {
      const openSubs = openSubRequestsForDateKey(selectedDateKey);
      if (openSubs.length > 0) {
        setConfirmState({
          open: true,
          title: 'Cancel sub request?',
          message:
            `You previously requested a sub for ${openSubs.length === 1 ? 'a class' : `${openSubs.length} classes`} on this date.\n\n`
            + 'Do you want to cancel the sub request now that you are available?',
          confirmLabel: 'Cancel + Save',
          secondaryLabel: 'Keep + Save',
          onConfirm: async () => {
            setConfirmState(s => ({ ...s, open: false }));
            try {
              setSaving(true);
              for (const ev of openSubs) {
                await cancelSubRequestForSquadEvent({
                  eventId: ev.id,
                  squadId: ev.squadId,
                  requestedByTeacherId: currentUser.uid,
                });
              }
              await performSaveException();
            } catch (e) {
              console.error('Failed to cancel and/or save', e);
              setErrors([e instanceof Error ? e.message : 'Failed to save. Please try again.']);
            } finally {
              setSaving(false);
            }
          },
          onSecondary: async () => {
            setConfirmState(s => ({ ...s, open: false }));
            try {
              setSaving(true);
              await performSaveException();
            } catch (e) {
              console.error('Failed to save exception', e);
              setErrors([e instanceof Error ? e.message : 'Failed to save. Please try again.']);
            } finally {
              setSaving(false);
            }
          },
        });
        return;
      }
    }

    try {
      setSaving(true);
      setErrors([]);
      await performSaveException();
    } catch (e) {
      console.error('Failed to save availability exception', e);
      setErrors(['Failed to save exception. Please try again.']);
    } finally {
      setSaving(false);
    }
  };

  const deleteException = async () => {
    if (!currentUser?.uid || !selectedDateKey || !selectedExceptionQuarterKey) return;
    const openSubs = openSubRequestsForDateKey(selectedDateKey);
    if (openSubs.length > 0) {
      setConfirmState({
        open: true,
        title: 'Cancel sub request too?',
        message:
          `You have an active sub request for ${openSubs.length === 1 ? 'a class' : `${openSubs.length} classes`} on this date.\n\n`
          + 'Deleting this exception will also cancel the sub request.',
        confirmLabel: 'Cancel sub request + Delete',
        onConfirm: async () => {
          setConfirmState(s => ({ ...s, open: false }));
          try {
            setSaving(true);
            for (const ev of openSubs) {
              await cancelSubRequestForSquadEvent({
                eventId: ev.id,
                squadId: ev.squadId,
                requestedByTeacherId: currentUser.uid,
              });
            }
            await performDeleteException();
          } catch (e) {
            console.error('Failed to cancel and/or delete', e);
            setErrors([e instanceof Error ? e.message : 'Failed to delete. Please try again.']);
          } finally {
            setSaving(false);
          }
        },
        onSecondary: undefined,
        secondaryLabel: undefined,
      });
      return;
    }
    try {
      setSaving(true);
      setErrors([]);
      await performDeleteException();
    } catch (e) {
      console.error('Failed to delete availability exception', e);
      setErrors(['Failed to delete exception. Please try again.']);
    } finally {
      setSaving(false);
    }
  };

  // Only teachers who currently hold an effective teaching role may request
  // coverage. This includes an incumbent substitute during a handoff.
  const holdsTeachingRole = (event: TeacherScheduleEvent | null): boolean => {
    if (!event || !currentUser?.uid) return false;
    const staffing = resolveEffectiveEventStaffing(event);
    return staffing.effectiveLeadTeacherId === currentUser.uid
      || staffing.effectiveSupportingTeacherIds.includes(currentUser.uid);
  };

  const openSubRequest = () => {
    setIsConflictModalOpen(false);
    setLogisticsModalOpen(true);
    setSubReason('schedule_conflict');
    setSubNote('');
    setSubNoteError(null);
  };

  const requestSubFromCalendar = (event: TeacherScheduleEvent) => {
    const dateKey = teacherDateKey(event.start);
    const q = quarterForDateKey(dateKey);
    if (!q) {
      setErrors(['No season is configured for this class date yet.']);
      return;
    }

    setSelectedDateKey(dateKey);
    setSelectedExceptionQuarterKey(q);
    setSelectedDayLabel(formatTimeInZone(event.start, teacherTz, 'EEE, MMM d, yyyy'));
    setExceptionDraft({
      status: 'unavailable',
      maybeReason: null,
      maybeNote: '',
      start: formatTimeInZone(event.start, teacherTz, 'HH:mm'),
      end: formatTimeInZone(event.end, teacherTz, 'HH:mm'),
    });
    setConflictEvent(event);
    setSubRequestedRole(resolveEffectiveEventStaffing(event).effectiveLeadTeacherId === currentUser?.uid ? 'lead' : 'co_teacher');
    setSelectedCalendarEvent(null);
    setSubReason('schedule_conflict');
    setSubNote('');
    setSubNoteError(null);
    setLogisticsModalOpen(true);
  };

  const cancelSubFromCalendar = (event: TeacherScheduleEvent) => {
    if (!currentUser?.uid) return;
    setConfirmState({
      open: true,
      title: 'Cancel sub request?',
      message:
        'This will remove the sub opportunity and restore the availability that existed before the request.',
      confirmLabel: 'Cancel sub request',
      onConfirm: async () => {
        setConfirmState(state => ({ ...state, open: false }));
        try {
          setSaving(true);
          await cancelSubRequestForSquadEvent({
            eventId: event.id,
            squadId: event.squadId,
            requestedByTeacherId: currentUser.uid,
          });
          setTeacherEvents(prev => prev.map(item => (
            item.id === event.id
              ? { ...item, subStatus: 'none', subRequestedByTeacherId: null }
              : item
          )));
          setSelectedCalendarEvent(null);
          setSaveMsg('Sub request canceled. Your previous availability was restored.');
        } catch (e) {
          console.error('Failed to cancel sub request', e);
          setErrors([e instanceof Error ? e.message : 'Failed to cancel the sub request. Please try again.']);
        } finally {
          setSaving(false);
        }
      },
    });
  };

  const submitSubRequest = async () => {
    if (!currentUser?.uid || !conflictEvent || !selectedDateKey || !selectedExceptionQuarterKey) return;

    const trimmedNote = String(subNote || '').trim();
    if (!trimmedNote) {
      setSubNoteError(
        'Teaching notes are required. Please add notes to help the substitute do a great job and have a good experience teaching this class.'
      );
      return;
    }

    setConfirmState({
      open: true,
      title: 'Request a sub for this class?',
      message:
        'You will remain responsible for this class until a sub claims it.\n\n'
        + 'We will also mark you as unavailable during this class time.',
      confirmLabel: 'Request sub',
      onConfirm: async () => {
        setConfirmState(s => ({ ...s, open: false }));
        try {
          setSubmittingSub(true);
          await requestSubForSquadEvent({
            eventId: conflictEvent.id,
            squadId: conflictEvent.squadId,
            requestedByTeacherId: currentUser.uid,
            requestedRole: subRequestedRole,
            classTitle: conflictEvent.title,
            classStart: conflictEvent.start,
            classEnd: conflictEvent.end,
            quarterKey: selectedExceptionQuarterKey,
            dateKey: selectedDateKey,
            availabilityTimeZone: teacherTz,
            reason: subReason,
            note: trimmedNote,
            requestedAvailabilityRangeLocal: {
              start: exceptionDraft.start,
              end: exceptionDraft.end,
            },
          });
          // Optimistic UI: mark this event as sub-requested immediately (so orange outline/badges show without reload).
          setTeacherEvents(prev => prev.map(ev => (
            ev.id === conflictEvent.id
              ? { ...ev, subStatus: 'requested' as any, subRequestedByTeacherId: currentUser.uid }
              : ev
          )));
          setIsSubModalOpen(false);
          setIsExceptionModalOpen(false);
          setSaveMsg('Sub requested. Availability updated.');
        } catch (e) {
          console.error('Failed to submit sub request', e);
          setErrors([e instanceof Error ? e.message : 'Failed to request a sub. Please try again.']);
        } finally {
          setSubmittingSub(false);
        }
      },
    });
  };

  if (!userData || userData.role !== 'teacher') {
    return (
      <div className={`ta-root ${colorblindAccessible ? 'is-colorblind' : ''}`}>
        <button type="button" className="ta-back" onClick={onBack}>← Back to Dashboard</button>
        <div className="ta-card">
          <div className="ta-title">Availability</div>
          <div className="ta-sub">Only teachers can edit availability.</div>
        </div>
      </div>
    );
  }

  return (
    <div className={`ta-root ta-season-${quarterKey} ${isDirty ? 'ta-root-has-sticky' : ''} ${colorblindAccessible ? 'is-colorblind' : ''}`}>
      <div className="ta-top">
        <button type="button" className="ta-back" onClick={onBack}>← Back to Dashboard</button>
        <div className="ta-title-row">
          <div className="ta-page-title">Availability</div>
          <label className="ta-accessibility-row ta-accessibility-row--top">
            <span className="ta-accessibility-text">
              <span className="ta-accessibility-title">Colorblind Accessible</span>
            </span>
            <span className="ta-switch">
              <input
                type="checkbox"
                role="switch"
                aria-label="Colorblind Accessible"
                checked={colorblindAccessible}
                onChange={(e) => setColorblindAccessible(e.target.checked)}
              />
              <span className="ta-switch-ui" aria-hidden="true" />
            </span>
          </label>
        </div>

        <div className="ta-season-banner" aria-live="polite">
          <span className="ta-season-banner-emoji" aria-hidden="true">{activeQuarter.emoji}</span>
          <div className="ta-season-banner-copy">
            <span className="ta-season-banner-eyebrow">Weekly availability season</span>
            <strong>You are updating {activeQuarter.label}</strong>
            <span>Changes below apply to your repeating weekly availability for this season.</span>
          </div>
          {quarterKey === defaultQuarterKey && (
            <span className="ta-season-banner-badge">✨ Next season</span>
          )}
        </div>

        <div className="ta-quarter-row">
          <label className="ta-label">
            🗓️ Season to update
            <select
              className="ta-select"
              value={quarterKey}
              onChange={(e) => onSwitchQuarter(e.target.value as AvailabilityQuarterKey)}
            >
              {selectableQuarters.map(q => (
                <option key={q.key} value={q.key}>{q.emoji} {q.label}</option>
              ))}
            </select>
          </label>
          <button
            type="button"
            className="ta-btn"
            disabled={!QUARTERS.find(q => q.key === quarterKey)?.prevKey}
            onClick={onCopyFromPrevious}
            title={!QUARTERS.find(q => q.key === quarterKey)?.prevKey ? 'No previous season available' : 'Copy from previous season'}
          >
            Copy from previous season
          </button>
        </div>

        {/* (helper text removed per request) */}
      </div>

      {errors.length > 0 && (
        <div className="ta-errors" role="alert">
          <div className="ta-errors-title">Please fix:</div>
          <ul className="ta-errors-list">
            {errors.map((e, i) => <li key={i}>{e}</li>)}
          </ul>
        </div>
      )}

      {saveMsg && (
        <div className="ta-saved" role="status">{saveMsg}</div>
      )}

      <div className="ta-grid">
          {DAYS.map(d => {
            const day = activeWeek[d.key];
            const blocks = normalizeBlocks(Array.isArray(day.blocks) ? day.blocks : []);
            const timeDetails = blocks[0] || { start: d.start, end: d.end, status: day.status };
            const dayStatusClass = day.status === 'available'
              ? 'is-available'
              : day.status === 'maybe'
                ? 'is-maybe'
                : 'is-unavailable';
            return (
              <div key={String(d.key)} className={`ta-day ${dayStatusClass}`}>
                <div className="ta-day-head">
                  <div className="ta-day-name">{d.label}</div>
                  <div className="ta-day-time-row" aria-label="Change time details">
                    <input
                      type="time"
                      className="ta-day-time-input"
                      value={timeDetails.start}
                      onChange={(e) => updateTimeRange(d.key, { start: e.target.value })}
                      title="Start time"
                    />
                    <span className="ta-day-time-sep">–</span>
                    <input
                      type="time"
                      className="ta-day-time-input"
                      value={timeDetails.end}
                      onChange={(e) => updateTimeRange(d.key, { end: e.target.value })}
                      title="End time"
                    />
                  </div>
                </div>

                <div className="ta-status">
                  <button
                    type="button"
                    className={`ta-pill ${day.status === 'available' ? 'is-on' : ''}`}
                    onClick={() => updateDay(d.key, { status: 'available' })}
                  >
                    ✅ Available
                  </button>
                  <button
                    type="button"
                    className={`ta-pill ${day.status === 'maybe' ? 'is-on is-maybe' : ''}`}
                    onClick={() => updateDay(d.key, { status: 'maybe' })}
                  >
                    ⚠️ Maybe
                  </button>
                  <button
                    type="button"
                    className={`ta-pill ${day.status === 'unavailable' ? 'is-on is-off' : ''}`}
                    onClick={() => updateDay(d.key, { status: 'unavailable' })}
                  >
                    ❌ Not available
                  </button>
                </div>

                {dayHasMaybe(day) && (
                  <div className="ta-maybe">
                    <label className="ta-label">
                      Reason (required)
                      <select
                        className="ta-select"
                        value={day.maybeReason || ''}
                        onChange={(e) => updateDay(d.key, { maybeReason: (e.target.value || null) as any })}
                      >
                        <option value="">Select…</option>
                        {MAYBE_REASONS.map(r => (
                          <option key={r.value} value={r.value}>{r.label}</option>
                        ))}
                      </select>
                    </label>
                    <label className="ta-label">
                      Note (optional)
                      <input
                        className="ta-input"
                        type="text"
                        value={day.maybeNote || ''}
                        onChange={(e) => updateDay(d.key, { maybeNote: e.target.value })}
                        placeholder="Any extra context…"
                      />
                    </label>
                  </div>
                )}

                {/* Time details are edited inline in the card header (top-right) */}
              </div>
            );
          })}
      </div>

      <div className="ta-exc">
          <div className="ta-exc-card">
            <div className="ta-exc-title">Specific Exceptions</div>
            <div className="ta-exc-legend">
              <span className="ta-legend-item"><span className="ta-legend-dot ta-legend-exc-available" /> Available</span>
              <span className="ta-legend-item"><span className="ta-legend-dot ta-legend-exc-maybe" /> Maybe</span>
              <span className="ta-legend-item"><span className="ta-legend-dot ta-legend-exc-unavailable" /> Not available</span>
              <span className="ta-legend-item"><span className="ta-legend-dot ta-legend-class" /> Scheduled class</span>
              {loadingEvents && <span className="ta-legend-item">Loading classes…</span>}
            </div>
            <div className="ta-exc-help">Use this calendar to mark specific days where your availability is different from your weekly schedule.</div>
            <div className="ta-calendar-wrap">
              <FullCalendar
                plugins={[dayGridPlugin, interactionPlugin]}
                initialView="dayGridMonth"
                timeZone={teacherTz}
                headerToolbar={{ left: 'prev,next today', center: 'title', right: '' }}
                height="auto"
                fixedWeekCount={false}
                showNonCurrentDates={false}
                dayMaxEventRows={3}
                eventTimeFormat={{ hour: 'numeric', minute: '2-digit', meridiem: 'short' }}
                datesSet={(arg) => {
                  try {
                    // Use the midpoint of the visible range to avoid edge cases where the visible range
                    // starts in the previous month (depending on FullCalendar config).
                    const mid = new Date((arg.start.getTime() + arg.end.getTime()) / 2);
                    setExceptionsViewAnchorDate(mid);
                  } catch (_) {}
                }}
                events={teacherEvents.map(ev => ({
                  id: ev.id,
                  title: ev.title,
                  start: fullCalendarWallTime(ev.start, teacherTz),
                  end: fullCalendarWallTime(ev.end, teacherTz),
                  extendedProps: {
                    kind: 'class',
                    dateKey: teacherDateKey(ev.start),
                    subStatus: ev.subStatus || 'none',
                    subRequestedByTeacherId: ev.subRequestedByTeacherId || null,
                  },
                }))}
                dateClick={(arg) => {
                  const dateKey = String((arg as any)?.dateStr || '') || ymdFromUtcDate(arg.date);
                  openExceptionEditorForDateKey(dateKey);
                }}
                eventClick={(info) => {
                  try {
                    info.jsEvent?.preventDefault?.();
                    info.jsEvent?.stopPropagation?.();
                  } catch (_) {}
                  const event = teacherEvents.find(item => item.id === info.event.id) || null;
                  if (event) setSelectedCalendarEvent(event);
                }}
                dayCellClassNames={(arg) => {
                  const dk = String((arg as any)?.dateStr || '') || ymdFromUtcDate(arg.date);
                  const q = exceptionsQuarterKey;
                  const has = !!(q && exceptions[q]?.[dk]);
                  const hasMySubRequest = !!(
                    currentUser?.uid
                    && teacherEvents.some(ev => teacherDateKey(ev.start) === dk && ev.subStatus === 'requested' && ev.subRequestedByTeacherId === currentUser.uid)
                  );

                  // If no weekly template exists for the quarter, leave base cells uncolored.
                  // Exceptions still color the day, since they are explicit overrides.
                  const st = effectiveStatusForDateKey(dk, q);
                  const statusClass = st
                    ? (st === 'available' ? 'ta-cal-available' : st === 'maybe' ? 'ta-cal-maybe' : 'ta-cal-unavailable')
                    : null;

                  const classes: string[] = [];
                  if (has) {
                    const exSt = exceptionStatusForDateKey(dk, q) || 'unavailable';
                    const dotClass = exSt === 'available' ? 'ta-exc-dot-available' : exSt === 'maybe' ? 'ta-exc-dot-maybe' : 'ta-exc-dot-unavailable';
                    classes.push('ta-has-exception', dotClass);
                  }
                  if (hasMySubRequest) classes.push('ta-has-sub-request');
                  if (statusClass) classes.push(statusClass);
                  return classes;
                }}
                eventDidMount={(info) => {
                  // Add a small dot marker for classes
                  try {
                    (info.el as HTMLElement).classList.add('ta-class-event');
                  } catch (_) {}

                  // Highlight the final scheduled class event (latest date)
                  try {
                    if (finalClassDayKey && info.event.extendedProps?.dateKey === finalClassDayKey) {
                      (info.el as HTMLElement).classList.add('ta-final-day-event');
                    }
                  } catch (_) {}

                  // Mark sub-requested classes (requested by the current teacher)
                  try {
                    const ex: any = info.event.extendedProps || {};
                    const isMine = currentUser?.uid && ex.subStatus === 'requested' && ex.subRequestedByTeacherId === currentUser.uid;
                    if (!isMine) return;
                    const el = info.el as HTMLElement;
                    el.classList.add('ta-class-event-sub-requested');
                  } catch (_) {}
                }}
              />
            </div>
          </div>
      </div>

      {isDirty && (
        <div className="ta-sticky-bar" role="region" aria-label="Unsaved changes actions">
          <div className="ta-sticky-inner">
            <div className="ta-sticky-text">You have unsaved changes</div>
            <div className="ta-sticky-actions">
              <button type="button" className="ta-btn" onClick={onCancelWeeklyChanges} disabled={saving}>
                Cancel
              </button>
              <button type="button" className="ta-btn ta-btn-primary" onClick={onSave} disabled={saving}>
                {saving ? 'Saving…' : 'Save Changes'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Scheduled class actions */}
      {selectedCalendarEvent && (
        <div className="ta-modal-overlay" onClick={() => setSelectedCalendarEvent(null)}>
          <div className="ta-modal ta-calendar-event-modal" onClick={(e) => e.stopPropagation()}>
            <div className="ta-modal-title">{selectedCalendarEvent.title}</div>
            <div className="ta-modal-sub">
              {formatTimeInZone(selectedCalendarEvent.start, teacherTz, 'EEE, MMM d, yyyy')} ·{' '}
              {formatTimeInZone(selectedCalendarEvent.start, teacherTz, 'h:mm a')}–
              {formatTimeInZone(selectedCalendarEvent.end, teacherTz, 'h:mm a')} {teacherTzLabel}
            </div>

            {selectedCalendarEvent.subStatus === 'requested' && (
              <div className="ta-calendar-event-status">Sub requested</div>
            )}
            {selectedCalendarEvent.subStatus === 'assigned' && (
              <div className="ta-calendar-event-status">A substitute has claimed this class.</div>
            )}

            <div className="ta-modal-actions ta-calendar-event-actions">
              <button
                type="button"
                className="ta-btn"
                onClick={() => navigate(`/app/squads/${selectedCalendarEvent.squadId}`)}
              >
                View Squad
              </button>

              {selectedCalendarEvent.subStatus === 'requested'
                && selectedCalendarEvent.subRequestedByTeacherId === currentUser?.uid ? (
                  <button
                    type="button"
                    className="ta-btn ta-btn-primary"
                    onClick={() => cancelSubFromCalendar(selectedCalendarEvent)}
                    disabled={saving}
                  >
                    Cancel sub request
                  </button>
                ) : selectedCalendarEvent.subStatus !== 'assigned'
                  && selectedCalendarEvent.subStatus !== 'requested'
                  && holdsTeachingRole(selectedCalendarEvent) ? (
                    <button
                      type="button"
                      className="ta-btn ta-btn-primary"
                      onClick={() => requestSubFromCalendar(selectedCalendarEvent)}
                    >
                      Request a Sub
                    </button>
                  ) : null}

              <button type="button" className="ta-btn" onClick={() => setSelectedCalendarEvent(null)}>
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Exception editor modal */}
      {isExceptionModalOpen && selectedDateKey && (
        <div className="ta-modal-overlay" onClick={closeExceptionEditor}>
          <div className="ta-modal" onClick={(e) => e.stopPropagation()}>
            <div className="ta-modal-title">{selectedDayLabel}</div>
            <div className="ta-modal-sub">Override weekly availability for this date ({teacherTzLabel}).</div>

            <div className="ta-form">
              <div className="ta-exc-modal-head">
                <div className="ta-exc-modal-time" aria-label="Change time details">
                  <input
                    type="time"
                    className="ta-day-time-input"
                    value={exceptionDraft.start}
                    onChange={(e) => setExceptionDraft(prev => ({ ...prev, start: e.target.value }))}
                    title="Start time"
                  />
                  <span className="ta-day-time-sep">–</span>
                  <input
                    type="time"
                    className="ta-day-time-input"
                    value={exceptionDraft.end}
                    onChange={(e) => setExceptionDraft(prev => ({ ...prev, end: e.target.value }))}
                    title="End time"
                  />
                  <span className="ta-exc-window-hint">Any time of day</span>
                </div>
              </div>

              <div className="ta-status">
                <button
                  type="button"
                  className={`ta-pill ${exceptionDraft.status === 'available' ? 'is-on' : ''}`}
                  onClick={() => setExceptionDraft(prev => ({ ...prev, status: 'available' }))}
                >
                  ✅ Available
                </button>
                <button
                  type="button"
                  className={`ta-pill ${exceptionDraft.status === 'maybe' ? 'is-on is-maybe' : ''}`}
                  onClick={() => setExceptionDraft(prev => ({ ...prev, status: 'maybe' }))}
                >
                  ⚠️ Maybe
                </button>
                <button
                  type="button"
                  className={`ta-pill ${exceptionDraft.status === 'unavailable' ? 'is-on is-off' : ''}`}
                  onClick={() => setExceptionDraft(prev => ({ ...prev, status: 'unavailable' }))}
                >
                  ❌ Not available
                </button>
              </div>

              {exceptionDraft.status === 'maybe' && (
                <div className="ta-maybe">
                  <label className="ta-label">
                    Reason (required)
                    <select
                      className="ta-select"
                      value={exceptionDraft.maybeReason || ''}
                      onChange={(e) => setExceptionDraft(prev => ({ ...prev, maybeReason: (e.target.value || null) as any }))}
                    >
                      <option value="">Select…</option>
                      {MAYBE_REASONS.map(r => (
                        <option key={r.value} value={r.value}>{r.label}</option>
                      ))}
                    </select>
                  </label>
                  <label className="ta-label">
                    Note (optional)
                    <input
                      className="ta-input"
                      type="text"
                      value={exceptionDraft.maybeNote || ''}
                      onChange={(e) => setExceptionDraft(prev => ({ ...prev, maybeNote: e.target.value }))}
                      placeholder="Any extra context…"
                    />
                  </label>
                </div>
              )}
            </div>

            <div className="ta-modal-actions">
              <button
                type="button"
                className="ta-btn"
                onClick={deleteException}
                disabled={saving || !selectedExceptionQuarterKey || !exceptions[selectedExceptionQuarterKey]?.[selectedDateKey]}
                title={!selectedExceptionQuarterKey || !exceptions[selectedExceptionQuarterKey]?.[selectedDateKey] ? 'No exception to delete' : 'Delete exception'}
              >
                Delete
              </button>
              <div className="ta-modal-actions-right">
                <button type="button" className="ta-btn" onClick={closeExceptionEditor} disabled={saving}>Cancel</button>
                <button type="button" className="ta-btn ta-btn-primary" onClick={saveException} disabled={saving}>
                  {saving ? 'Saving…' : 'Save exception'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Conflict modal */}
      {isConflictModalOpen && conflictEvent && selectedDateKey && (
        <div className="ta-modal-overlay" onClick={() => { setIsConflictModalOpen(false); }}>
          <div className="ta-modal" onClick={(e) => e.stopPropagation()}>
            <div className="ta-modal-title">This exception conflicts with a scheduled class.</div>
            <div className="ta-modal-sub">
              <b>{conflictEvent.title}</b><br />
              {formatTimeInZone(conflictEvent.start, teacherTz, 'EEE, MMM d')} ·{' '}
              {formatTimeInZone(conflictEvent.start, teacherTz, 'h:mm a')}–{formatTimeInZone(conflictEvent.end, teacherTz, 'h:mm a')} {teacherTzLabel}
            </div>
            <div className="ta-modal-actions">
              {holdsTeachingRole(conflictEvent) && (
                <button type="button" className="ta-btn ta-btn-primary" onClick={openSubRequest}>Request a Sub</button>
              )}
              <button type="button" className="ta-btn" onClick={() => setIsConflictModalOpen(false)}>Edit Exception</button>
              <button
                type="button"
                className="ta-btn"
                onClick={() => { setIsConflictModalOpen(false); setIsExceptionModalOpen(false); }}
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Request sub modal */}
      {isSubModalOpen && conflictEvent && (
        <div className="ta-modal-overlay" onClick={() => setIsSubModalOpen(false)}>
          <div className="ta-modal" onClick={(e) => e.stopPropagation()}>
            <div className="ta-modal-title">Request a Sub</div>
            <div className="ta-modal-sub">
              For <b>{conflictEvent.title}</b> · {formatTimeInZone(conflictEvent.start, teacherTz, 'EEE, MMM d')} ·{' '}
              {formatTimeInZone(conflictEvent.start, teacherTz, 'h:mm a')}–{formatTimeInZone(conflictEvent.end, teacherTz, 'h:mm a')} {teacherTzLabel}
            </div>

            <div className="ta-form">
              {resolveEffectiveEventStaffing(conflictEvent).effectiveLeadTeacherId === currentUser?.uid && resolveEffectiveEventStaffing(conflictEvent).effectiveSupportingTeacherIds.includes(String(currentUser?.uid || '')) && (
                <label className="ta-label">
                  Role needing coverage
                  <select className="ta-select" value={subRequestedRole} onChange={(e) => setSubRequestedRole(e.target.value as 'lead' | 'co_teacher')}>
                    <option value="lead">Lead teacher</option>
                    <option value="co_teacher">Co-teacher</option>
                  </select>
                </label>
              )}
              <label className="ta-label">
                Reason
                <select className="ta-select" value={subReason} onChange={(e) => setSubReason(e.target.value as SubRequestReason)}>
                  <option value="schedule_conflict">Schedule conflict</option>
                  <option value="illness">Illness</option>
                  <option value="travel">Travel</option>
                  <option value="other">Other</option>
                </select>
              </label>
              <label className="ta-label">
                Teaching notes (required)
                <div className="ta-help">
                  These notes are meant to help your substitute do a great job and have a good experience teaching. Include what’s worked with
                  challenging behaviors, what each student is like, classroom routines, and anything else they should know coming in.
                </div>
                <textarea
                  className="ta-textarea"
                  rows={7}
                  value={subNote}
                  onChange={(e) => {
                    setSubNote(e.target.value);
                    if (subNoteError) setSubNoteError(null);
                  }}
                  placeholder={
                    "Examples to include:\n"
                    + "- Student behavior notes + what’s worked\n"
                    + "- Helpful routines / expectations\n"
                    + "- Seating / grouping tips\n"
                    + "- Any safety or pickup/dropoff nuances"
                  }
                />
                {subNoteError && <div className="ta-error" role="alert">{subNoteError}</div>}
              </label>
            </div>

            <div className="ta-modal-actions">
              <button type="button" className="ta-btn" onClick={() => setIsSubModalOpen(false)} disabled={submittingSub}>Cancel</button>
              <button type="button" className="ta-btn ta-btn-primary" onClick={submitSubRequest} disabled={submittingSub}>
                {submittingSub ? 'Submitting…' : 'Submit'}
              </button>
            </div>
          </div>
        </div>
      )}

      <ConfirmationModal
        isOpen={confirmState.open}
        onClose={() => setConfirmState(s => ({ ...s, open: false }))}
        onConfirm={() => confirmState.onConfirm()}
        onSecondary={confirmState.onSecondary}
        title={confirmState.title}
        message={confirmState.message}
        confirmText={confirmState.confirmLabel || 'Confirm'}
        secondaryText={confirmState.secondaryLabel}
        cancelText="Cancel"
      />

      {conflictEvent && (
        <RequiredTeacherLogisticsModal
          isOpen={logisticsModalOpen}
          squadId={conflictEvent.squadId}
          currentUserId={currentUser?.uid}
          onClose={() => {
            setLogisticsModalOpen(false);
            // Return them to the conflict prompt so they can choose again.
            setIsConflictModalOpen(true);
          }}
          onContinue={() => {
            setLogisticsModalOpen(false);
            setIsSubModalOpen(true);
          }}
        />
      )}
    </div>
  );
};

export default TeacherAvailabilityPage;
